import { TestType } from "types/testTypes";
import {
  ClinicalSummaryInterpretation,
  ClinicalSummaryMetric,
  ClinicalRiskLevel,
} from "./clinicalSummaryTypes";

export type PupillometryMetricRow = { label: string; value: number | null; unit: string }[] | null;

interface PupillometryInterpretationInput {
  testType?: TestType | null;
  leftResults?: PupillometryMetricRow;
  rightResults?: PupillometryMetricRow;
  leftResultsDirect?: PupillometryMetricRow;
  rightResultsDirect?: PupillometryMetricRow;
  leftResultsConsensual?: PupillometryMetricRow;
  rightResultsConsensual?: PupillometryMetricRow;
}

const PUPILLOMETRY_THRESHOLDS = {
  anisocoria: { mildHigh: 1.5, severeHigh: 2.2 },
  latency: { mildHigh: 0.5, severeHigh: 0.7 },
  latencyDiff: { mildHigh: 0.18, severeHigh: 0.26 },
  changePercent: { mildLow: 9, severeLow: 5 },
  constrictionVelocity: { mildLow: 0.4, severeLow: 0.22 },
  dilationVelocity: { mildLow: 0.15, severeLow: 0.07 },
  rapdChangeDiff: { mildHigh: 24, severeHigh: 36 },
  rapdLatencyDiff: { mildHigh: 0.18, severeHigh: 0.26 },
} as const;

const getMetricValue = (results: PupillometryMetricRow, label: string): number | null => {
  const item = results?.find((metric) => metric.label === label);
  return typeof item?.value === "number" && Number.isFinite(item.value) ? item.value : null;
};

const formatMetric = (
  labelKey: string,
  value: number | string | null,
  unit?: string,
  decimals = 2,
): ClinicalSummaryMetric => ({
  labelKey,
  value,
  unit,
  decimals,
});

const formatNumber = (value: number | null, decimals = 2, unit = "") => {
  if (value === null) return "—";
  return `${value.toFixed(decimals)}${unit}`;
};

const addSeverity = (
  value: number | null,
  thresholds: { mildHigh?: number; severeHigh?: number; mildLow?: number; severeLow?: number },
): "none" | "mild" | "severe" => {
  if (value === null) return "none";

  if (typeof thresholds.severeHigh === "number" && value > thresholds.severeHigh) {
    return "severe";
  }
  if (typeof thresholds.severeLow === "number" && value < thresholds.severeLow) {
    return "severe";
  }
  if (typeof thresholds.mildHigh === "number" && value > thresholds.mildHigh) {
    return "mild";
  }
  if (typeof thresholds.mildLow === "number" && value < thresholds.mildLow) {
    return "mild";
  }

  return "none";
};

const getHighestSeverity = (
  severities: Array<"none" | "mild" | "severe">,
): "none" | "mild" | "severe" => {
  if (severities.includes("severe")) return "severe";
  if (severities.includes("mild")) return "mild";
  return "none";
};

const getRiskLevelFromBuckets = (buckets: Array<"none" | "mild" | "severe">): ClinicalRiskLevel => {
  const severeCount = buckets.filter((severity) => severity === "severe").length;
  const mildCount = buckets.filter((severity) => severity === "mild").length;

  if (severeCount >= 2 || (severeCount >= 1 && mildCount >= 2)) {
    return "high";
  }

  if (severeCount === 1 || mildCount >= 2) {
    return "monitor";
  }

  return "low";
};

export const interpretPupillometrySummary = ({
  testType,
  leftResults,
  rightResults,
  leftResultsDirect,
  rightResultsDirect,
  leftResultsConsensual,
  rightResultsConsensual,
}: PupillometryInterpretationInput): ClinicalSummaryInterpretation | null => {
  const isRapd = testType === TestType.RAPD;

  const standardLeft = isRapd ? leftResultsDirect : leftResults;
  const standardRight = isRapd ? rightResultsDirect : rightResults;
  if (!standardLeft || !standardRight) return null;

  const leftSize = getMetricValue(standardLeft, "size");
  const rightSize = getMetricValue(standardRight, "size");
  const leftLatency = getMetricValue(standardLeft, "lat");
  const rightLatency = getMetricValue(standardRight, "lat");
  const leftChange = getMetricValue(standardLeft, "ch");
  const rightChange = getMetricValue(standardRight, "ch");
  const leftConstrictionVelocity =
    getMetricValue(standardLeft, "mcv") ?? getMetricValue(standardLeft, "cv");
  const rightConstrictionVelocity =
    getMetricValue(standardRight, "mcv") ?? getMetricValue(standardRight, "cv");
  const leftDilationVelocity = getMetricValue(standardLeft, "dv");
  const rightDilationVelocity = getMetricValue(standardRight, "dv");

  const anisocoria =
    leftSize !== null && rightSize !== null ? Math.abs(leftSize - rightSize) : null;
  const worstLatency =
    leftLatency !== null && rightLatency !== null
      ? Math.max(leftLatency, rightLatency)
      : (leftLatency ?? rightLatency);
  const latencyDifference =
    leftLatency !== null && rightLatency !== null ? Math.abs(leftLatency - rightLatency) : null;
  const weakestChange =
    leftChange !== null && rightChange !== null
      ? Math.min(leftChange, rightChange)
      : (leftChange ?? rightChange);
  const weakestConstrictionVelocity =
    leftConstrictionVelocity !== null && rightConstrictionVelocity !== null
      ? Math.min(leftConstrictionVelocity, rightConstrictionVelocity)
      : (leftConstrictionVelocity ?? rightConstrictionVelocity);
  const weakestRecoverySpeed =
    leftDilationVelocity !== null && rightDilationVelocity !== null
      ? Math.min(leftDilationVelocity, rightDilationVelocity)
      : (leftDilationVelocity ?? rightDilationVelocity);

  const directChangeDifference =
    isRapd && leftChange !== null && rightChange !== null
      ? Math.abs(leftChange - rightChange)
      : null;
  const consensualLeftChange = getMetricValue(leftResultsConsensual, "ch");
  const consensualRightChange = getMetricValue(rightResultsConsensual, "ch");
  const weakestConsensualChange =
    consensualLeftChange !== null && consensualRightChange !== null
      ? Math.min(consensualLeftChange, consensualRightChange)
      : (consensualLeftChange ?? consensualRightChange);

  const riskBuckets = isRapd
    ? [
        addSeverity(anisocoria, PUPILLOMETRY_THRESHOLDS.anisocoria),
        getHighestSeverity([
          addSeverity(directChangeDifference, PUPILLOMETRY_THRESHOLDS.rapdChangeDiff),
          addSeverity(latencyDifference, PUPILLOMETRY_THRESHOLDS.rapdLatencyDiff),
        ]),
        getHighestSeverity([
          addSeverity(weakestChange, PUPILLOMETRY_THRESHOLDS.changePercent),
          addSeverity(weakestConsensualChange, PUPILLOMETRY_THRESHOLDS.changePercent),
        ]),
      ]
    : [
        addSeverity(anisocoria, PUPILLOMETRY_THRESHOLDS.anisocoria),
        getHighestSeverity([
          addSeverity(worstLatency, PUPILLOMETRY_THRESHOLDS.latency),
          addSeverity(latencyDifference, PUPILLOMETRY_THRESHOLDS.latencyDiff),
        ]),
        getHighestSeverity([
          addSeverity(weakestChange, PUPILLOMETRY_THRESHOLDS.changePercent),
          addSeverity(weakestConstrictionVelocity, PUPILLOMETRY_THRESHOLDS.constrictionVelocity),
          addSeverity(weakestRecoverySpeed, PUPILLOMETRY_THRESHOLDS.dilationVelocity),
        ]),
      ];

  const riskLevel = getRiskLevelFromBuckets(riskBuckets);

  const keyFindings = isRapd
    ? [
        `Baseline size difference was ${formatNumber(anisocoria, 2, " mm")}.`,
        `Direct response difference was ${formatNumber(directChangeDifference, 1, "%")}, with latency difference of ${formatNumber(latencyDifference, 2, " s")}.`,
        `Weaker direct response was ${formatNumber(weakestChange, 1, "%")}; weaker consensual response was ${formatNumber(weakestConsensualChange, 1, "%")}.`,
      ]
    : [
        `Baseline size difference was ${formatNumber(anisocoria, 2, " mm")}.`,
        `Slowest latency was ${formatNumber(worstLatency, 2, " s")}; weakest constriction was ${formatNumber(weakestChange, 1, "%")}.`,
        `Slower constriction speed was ${formatNumber(weakestConstrictionVelocity, 2, " mm/s")}; recovery speed was ${formatNumber(weakestRecoverySpeed, 2, " mm/s")}.`,
      ];

  const interpretationNotes = isRapd
    ? [
        "A weaker direct response can suggest less incoming light signal from that eye.",
        "Timing differences are usually checked with repeatability and symptoms.",
        "RAPD can point to uneven optic nerve or retinal function, but not to a diagnosis by itself.",
      ]
    : [
        "Small left-right differences can happen in normal testing.",
        "Slower timing matters more when it appears with weaker constriction or asymmetry.",
        "These results are best read with the rest of the eye exam.",
      ];

  return {
    riskLevel,
    titleKey: "extraTests.pupillometry.summary.title",
    explainerKey: `extraTests.pupillometry.summary.explainer.${isRapd ? "rapd" : "standard"}`,
    reasonKey: `extraTests.pupillometry.summary.reasons.${isRapd ? "rapd" : "standard"}.${riskLevel}`,
    keyFindings,
    interpretationNotes,
    metrics: isRapd
      ? [
          formatMetric(
            "extraTests.pupillometry.summary.metrics.baselineAnisocoria",
            anisocoria,
            "mm",
            2,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.directChangeDifference",
            directChangeDifference,
            "%",
            1,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.latencyDifference",
            latencyDifference,
            "s",
            2,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.weakerDirectResponse",
            weakestChange,
            "%",
            1,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.weakerConsensualResponse",
            weakestConsensualChange,
            "%",
            1,
          ),
        ]
      : [
          formatMetric(
            "extraTests.pupillometry.summary.metrics.baselineAnisocoria",
            anisocoria,
            "mm",
            2,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.worstLatency",
            worstLatency,
            "s",
            2,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.weakestConstriction",
            weakestChange,
            "%",
            1,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.constrictionSpeed",
            weakestConstrictionVelocity,
            "mm/s",
            2,
          ),
          formatMetric(
            "extraTests.pupillometry.summary.metrics.recoverySpeed",
            weakestRecoverySpeed,
            "mm/s",
            2,
          ),
        ],
  };
};
